import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Lock, GraduationCap, BookOpen, ClipboardList, BarChart3, Brain, CalendarClock, Timer } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Featured", hash: "#featured" },
  { label: "Store", hash: "#catalog" },
  { label: "Previews", hash: "#previews" },
  { label: "YouTube", hash: "#youtube" },
  { label: "Enquiry", hash: "#enquiry" },
];

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(hash);
  };

  return (
    <header className="fixed top-4 inset-x-0 z-50 px-4" data-testid="site-header">
      <div className={`mx-auto max-w-7xl flex items-center justify-between gap-4 rounded-2xl border px-4 sm:px-6 py-3 backdrop-blur-2xl transition-all duration-300 ${scrolled ? "bg-ink/85 border-white/10 shadow-2xl shadow-black/20" : "bg-ink/40 border-white/10"}`}>
        <a href="#top" onClick={(e) => { e.preventDefault(); window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); }} className="shrink-0" data-testid="header-logo">
          <Logo />
        </a>
        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <a key={l.hash} href={l.hash} onClick={(e) => go(e, l.hash)}
              className="px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-amber-400 hover:bg-white/5 transition-colors">
              {l.label}
            </a>
          ))}
          <Link to="/practice" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-emerald-400 hover:bg-emerald-500/10 transition-colors" data-testid="nav-practice-link">
            <GraduationCap size={15} /> Practice
          </Link>
          <Link to="/test-series" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-sky-400 hover:bg-sky-500/10 transition-colors" data-testid="nav-testseries-link">
            <Timer size={15} /> Test Series
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <a href="/admin" data-testid="admin-panel-link"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
            <Lock size={14} /> Admin
          </a>
          <a href="#catalog" onClick={(e) => go(e, "#catalog")}
            className="hidden sm:inline-flex items-center rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-ink hover:bg-amber-400 transition-colors">
            Buy Notes
          </a>
          <button className="md:hidden p-2 text-slate-200" onClick={() => setOpen(!open)} data-testid="header-menu-btn" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden mx-auto max-w-7xl mt-2 rounded-2xl border border-white/10 bg-ink/95 backdrop-blur-2xl p-4 flex flex-col gap-1" data-testid="header-mobile-menu">
          {LINKS.map((l) => (
            <a key={l.hash} href={l.hash} onClick={(e) => go(e, l.hash)} className="px-4 py-3 rounded-xl text-sm font-medium text-slate-200 hover:bg-white/5">
              {l.label}
            </a>
          ))}
          <p className="mt-2 px-4 pt-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Saare Features</p>
          <Link to="/iq" className="px-4 py-3 rounded-xl text-sm font-bold text-amber-400 flex items-center gap-2" data-testid="nav-iq-mobile">
            <Brain size={15} /> IQ & Brain Challenge
          </Link>
          <Link to="/practice" className="px-4 py-3 rounded-xl text-sm font-bold text-emerald-400 flex items-center gap-2" data-testid="nav-practice-mobile">
            <GraduationCap size={15} /> Practice Tests (Free MCQs)
          </Link>
          <Link to="/test-series" className="px-4 py-3 rounded-xl text-sm font-bold text-sky-400 flex items-center gap-2" data-testid="nav-testseries-mobile">
            <Timer size={15} /> Test Series (Full & Subject Tests)
          </Link>
          <Link to="/blog" className="px-4 py-3 rounded-xl text-sm font-semibold text-slate-200 flex items-center gap-2 hover:bg-white/5" data-testid="nav-blog-mobile">
            <BookOpen size={15} /> Exam Tips Blog
          </Link>
          <Link to="/survey" className="px-4 py-3 rounded-xl text-sm font-semibold text-slate-200 flex items-center gap-2 hover:bg-white/5" data-testid="nav-survey-mobile">
            <ClipboardList size={15} /> Student Learning Survey
          </Link>
          <Link to="/crm" className="px-4 py-3 rounded-xl text-sm font-semibold text-slate-200 flex items-center gap-2 hover:bg-white/5" data-testid="nav-crm-mobile">
            <BarChart3 size={15} /> VOOGLY CRM (Institute)
          </Link>
          <Link to="/shiftsync" className="px-4 py-3 rounded-xl text-sm font-semibold text-slate-200 flex items-center gap-2 hover:bg-white/5" data-testid="nav-shiftsync-mobile">
            <CalendarClock size={15} /> ShiftSync (Duty Roster)
          </Link>
          <a href="/admin" className="px-4 py-3 rounded-xl text-sm font-semibold text-amber-400 flex items-center gap-2" data-testid="nav-admin-mobile">
            <Lock size={14} /> Admin Portal
          </a>
        </div>
      )}
    </header>
  );
}
